import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { addMonths } from 'date-fns';
import toast from 'react-hot-toast';
import { 
  useTheme, 
  Box,   
  Typography, 
  CircularProgress, 
  Tooltip, 
  IconButton, 
  ToggleButton, 
  ToggleButtonGroup, 
} from '@mui/material';
import { 
  Download,
  AddCircleOutline, 
  Edit,
  Save,
  Delete,
  DeleteForever,
  Assessment,
  Calculate,
  Monitor,
  ChangeHistory,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { fetchGroupedRosterDetailsByCrewCode } from "state/api";
import { formatTZDate } from "state/helpers";
import { 
  getSelectedIds, 
  isAnyRowSelected, 
  sanitizeData, 
  getAdjustedEndDate, 
  parseUTCDate,
} from 'state/helpers';
import { exportToExcel } from 'state/excel';
import { logClientAction } from 'state/logger';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import RosterDetailsDialog from 'components/RosterDetailsDialog';
import CrewAssessment from "./CrewAssessment";
import CrewKPI from "./CrewKPI";
import CrewKPIData from "./CrewKPIData";


const CrewPerformances = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user); 
  const theme = useTheme();
  
  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "CrewPerformances", "create", false);
  const canView = hasRight(permissionMap, "CrewPerformances", "view", false);
  const canEdit = hasRight(permissionMap, "CrewPerformances", "update", false);
  const canDelete = hasRight(permissionMap, "CrewPerformances", "delete", false);
  const canCalculate = hasRight(permissionMap, "CrewPerformances", "calculate", false);
  const canSave = hasRight(permissionMap, "CrewPerformances", "save", false);
  const canShow = hasRight(permissionMap, "CrewPerformances", "show", false);
  const canReadKpis = hasRight(permissionMap, "CrewKpis", "read", false);
  const canDeleteKpis = hasRight(permissionMap, "CrewKpis", "delete", false);
  
  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });
  const [openRosterDetailsDialog, setOpenRosterDetailsDialog] = useState(false);
  const [groupedCrewRosters, setGroupedCrewRosters] = useState([]);
  const [selectedCrewCode, setSelectedCrewCode] = useState(null);
  const [selectedCrewName, setSelectedCrewName] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'single' or 'bulk'
  const [idToDelete, setIdToDelete] = useState(null);
  const [view, setView] = useState('crewAssessment');
  const [crewPerformances, setCrewPerformances] = useState([]);
  const [crewKPI, setCrewKPI] = useState([]);
  const [crewKPIData, setCrewKPIData] = useState([]);


  useEffect(() => {
    const fetchCrewPerformances = async () => {
      try {
        if (!startDate || !endDate) return [];

        setError(null);
        setLoading(true);  

        const url = '/crewperformances';
        const payload = { startDate, endDate };
        const res = await api.post(url, payload);
        
        setCrewPerformances(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchCrewPerformances();
  }, [startDate, endDate]); 

  const calculateCrewKPIs = useCallback(async () => {      
    try {
      if (!canCalculate) return;

      if (!startDate || !endDate) return null; 

      setError(null);
      setLoading(true);

      const toDate = getAdjustedEndDate(endDate);

      const url = '/crewperformances/crewkpi';
      const payload = { startDate, endDate: toDate };
      const { data } = await api.post(url, payload);
      
      const updatedKpi = data.data?.map((cur) => {
        const list = crewPerformances
          ?.filter(item => item.crewCode === cur.crewCode)
          ?.sort((a, b) => new Date(b.date) - new Date(a.date));

        return {
          ...cur,
          kpi: list?.[0]?.kpi || 'A',
          description: list?.[0]?.description || '',
        };
      });

      const excludedFields = [
        'gender', 
        'rankType', 
        'trainingQuals', 
        'quals', 
        'airHours', 
        'instructorSimHours', 
        'totalHours',
        'avgTotalHours',
        'deviationTotalHours',
        'traineeSimHours',
        'perdiemHours',
        'domPerdiemHours',
        'intPerdiemHours',
        'arrCount',
        'flyingDaysCount',
        'flyingDaysSet',
        'nonFlyingDaysCount',
        'woclSectorsCount',
        'weekendRoutesCount',
        'sickOnHolidayAndWeekendCount',
        'fixedOffCount',
        'fixedOffOnHolidayAndWeekendCount',
        'lineTrainingBlockHours',
        'lineTrainingSectors',
        'observeBlockHours',
        'observeSectors',
        'isActive',
      ]
      const sanitizedKpi = sanitizeData(updatedKpi, excludedFields);

      setCrewKPI(sanitizedKpi || []);      
    } catch (error) {
      setError(error)
      toast.error(error?.message || "Failed to fetch crew KPI!");
    } finally {
      setLoading(false);
    }
  }, [
    startDate, 
    endDate, 
    canCalculate, 
    crewPerformances
  ]);
  
  const saveCrewKPI = useCallback(async () => {      
    try {
      if (!canSave) return;

      if (!startDate || !endDate) return null; 

      setError(null);
      setLoading(true);

      const excludedFields = [
        'dhdHours', 
        'dhdSectors', 
        'blockHours', 
        'sectors', 
        'standby', 
        'noShow', 
        'sick', 
        'codes',        
      ]
      const sanitizedKpi = sanitizeData(crewKPI, excludedFields);

      const url = '/crewkpis/new';
      const payload = { 
        startDate: parseUTCDate(startDate), 
        endDate: parseUTCDate(endDate), 
        crewKpi: sanitizedKpi 
      };
      await api.post(url, payload);

      toast.success("Save crew KPI successfully.");
    } catch (error) {
      setError(error)
      toast.error(error?.message || "Failed to fetch crew KPI!");
    } finally {
      setLoading(false);
    }
  }, [
    startDate, 
    endDate, 
    canSave, 
    crewKPI
  ]);

  const showSavedCrewKPI = useCallback(async () => {      
    try {
      if (!canShow) return;

      if (!startDate || !endDate) return null; 

      setError(null);
      setLoading(true);

      const url = '/crewkpis';
      const payload = { 
        startDate: parseUTCDate(startDate), 
        endDate: parseUTCDate(endDate),  
      };
      const savedKpi = await api.post(url, payload);

      setCrewKPI(savedKpi.data?.crewKpi || []);
    } catch (error) {
      setError(error)
      toast.error(error?.message || "Failed to show crew KPI!");
    } finally {
      setLoading(false);
    }
  }, [
    startDate, 
    endDate, 
    canShow
  ]);

  const handleCrewKPIData = useCallback(async () => {      
    try {
      if (!canReadKpis) return;

      if (!startDate || !endDate) return null; 

      setError(null);
      setLoading(true);

      const url = '/crewkpis/all';
      const payload = { 
        startDate: parseUTCDate(startDate), 
        endDate: parseUTCDate(endDate),  
      };
      const savedKpi = await api.post(url, payload);
      
      setCrewKPIData(savedKpi.data || []);
    } catch (error) {
      setError(error)
      toast.error(error?.message || "Failed to fetch crew KPI data!");
    } finally {
      setLoading(false);
    }
  }, [
    startDate, 
    endDate,
    canReadKpis, 
  ]);

  useEffect(() => {
    showSavedCrewKPI();
    handleCrewKPIData();
  }, [showSavedCrewKPI, handleCrewKPIData]); 

  const handleViewCrewRosters = useCallback(async (param) => {
    if (!canView) return;

    if (!startDate || !endDate) return [];

    const crewCode = param?.crewCode || '';
    const crewName = param?.crewName || '';
    setSelectedCrewCode(crewCode);
    setSelectedCrewName(crewName);
    let result = [];

    try {
      result = await fetchGroupedRosterDetailsByCrewCode(crewCode, addMonths(startDate, -1), addMonths(endDate, -1));       
      setGroupedCrewRosters(result);
      setOpenRosterDetailsDialog(true);
      return;
    } catch {      
      setOpenRosterDetailsDialog(false);      
    }
  }, [canView, startDate, endDate]);
  
  const handleCloseRosterDetailsDialog = () => {
    setGroupedCrewRosters([]);
    setOpenRosterDetailsDialog(false);
  };

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      width: 60,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    { field: "crewCode", headerName: "Code", width: 60 },
    { 
      field: "crewName", 
      headerName: "Crew Name", 
      width: 200,
      renderCell: (params) => {
        return (
          <Box
            sx={{              
              cursor: "pointer",
              '&:hover': {
                color: "primary.dark",
                textDecoration: 'underline',
              },
            }}
            onClick={() => { handleViewCrewRosters(params.row) }}
          >
            {params.value}
          </Box>
        );
      }, 
    },
    {
      field: "date",
      headerName: "Date",
      width: 100,
      valueFormatter: (value) => value ? formatTZDate(value, 'UTC', 'dd/MM/yyyy') : "",
    },
    { field: "performanceType", headerName: "PerformanceType", width: 120 },    
    { field: "level", headerName: "Level", width: 100 },    
    { field: "description", headerName: "Description", width: 280 },
    { field: "kpi", headerName: "KPI", width: 60 },     
    {
      field: "disciplinaryAction",
      headerName: "DisciplinaryAction",
      width: 180,
    },
    {
      field: "effectiveTo",
      headerName: "EffectiveTo",
      width: 120,
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : "",
    },        
  ], [handleViewCrewRosters]);

  const actionColumn = useMemo(() => {
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];
    const baseWidth = 105 - (2 - activeCount) * 20;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,      
        sortable: false,
        width: baseWidth,      
        renderCell: (params) => {        
          return (
            <FlexCenter
              gap={1}
              sx={{ width: '100%', height: '100%' }}
            >            
              {canEdit && (
                <Tooltip title="Edit">
                  <IconButton
                    aria-label="Edit"
                    sx={{
                      color: theme.palette.primary.dark,
                      transition: 'color 0.3s ease, background-color 0.3s ease',
                      '&:hover': {
                        color: theme.palette.green.deep,
                      },
                    }}
                    onClick={() => {
                      if (!canEdit) return;
                      navigate(`/crewperformances/${params.row._id}`)
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>    
              )}        
              {canDelete && (
                <Tooltip title="Delete">
                  <IconButton
                    aria-label="Delete"
                    sx={{
                      color: theme.palette.primary.dark,
                      transition: 'color 0.3s ease, background-color 0.3s ease',
                      '&:hover': {
                        color: theme.palette.green.deep,
                      },
                    }}
                    onClick={() => {
                      if (!canDelete) return;
                      handleDeleteClick(params.row._id);
                    }}
                  >
                    <DeleteForever />
                  </IconButton>                
                </Tooltip>          
              )} 
            </FlexCenter>
          );
        },
      },
    ]
  }, [navigate, theme, canEdit, canDelete]);
  
  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, crewPerformances);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        
        await api.delete(`/crewperformances/delete/${idToDelete}`);

        setCrewPerformances(crewPerformances.filter((item) => item._id !== idToDelete));
        toast.success('Crew performance deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, crewPerformances);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }
        const payload = { ids: selectedIds };
        await api.patch('/crewperformances/soft-delete', payload)

        setCrewPerformances(
          crewPerformances.filter(item => !selectedIds.includes(item._id))
        );
        toast.success(`${rowSelectionModel.ids.size} record(s) deleted successfully.`);
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete!");
    }
  }, [
    canDelete, 
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    crewPerformances
  ]);

  const handleKpiChange = (id, newKpi) => {
    setCrewKPI(prev =>
      prev.map(item =>
        item._id === id ? { ...item, kpi: newKpi } : item
      )
    );
  };

  const handleDeleteCrewKpi = useCallback(async (id) => {
    try {
      if (!canDeleteKpis) 
        return;
      
      setLoading(true);
      await api.delete(`/crewkpis/delete/${id}`);

      setCrewKPIData((prevLogs) => prevLogs.filter(log => log._id !== id));

      toast.success("Crew KPI deleted successfully.");
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to delete crew KPI.");
    } finally {
      setLoading(false);
    }
  }, [canDeleteKpis]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Crew Performances" 
          subtitle={`${loading ? 'Loading...' : 'List of Members'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {view === 'showKPI' && (
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title="Download Excel file" arrow>
                  <span>
                    <StyledButton 
                      onClick={() => {
                        const omitFields = ['approvedBy', 'approvedAt', 'deletedAt', 'deletedBy', 'updatedAt', 'createdAt', '__v']
                        exportToExcel(crewKPI, "CrewPerformances", omitFields);
                        logClientAction("EXPORT_REPORT", { fileName: "CrewPerformances", format: "Excel", startDate, endDate });
                      }}
                    >
                      <Download />
                    </StyledButton>
                  </span>              
                </Tooltip>
              </Box>
            )}
            {view === 'calcKPI' && (
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title="Save" arrow>
                  <span>
                    <StyledButton 
                      onClick={() => {
                        if (!canSave) return;
                        saveCrewKPI();
                      }}
                    >
                      <Save />
                    </StyledButton>
                  </span>              
                </Tooltip>
              </Box>
            )}
            {view === 'crewAssessment' && (
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title="Bulk soft delete" arrow>
                  <span>
                    <StyledButton 
                      sx={{ display: 'none' }}
                      disabled={!isAnyRowSelected(rowSelectionModel, crewPerformances) ||
                        !canDelete
                      }
                      onClick={() => {
                        if (!canDelete) return;
                        handleBulkDelete();
                      }}
                    >
                      <Delete />
                    </StyledButton>
                  </span>
                </Tooltip>
                
                <Tooltip title="Create" arrow>
                  <span>
                    <StyledButton 
                      disabled={!canCreate}
                      onClick={() => {
                        if (!canCreate) return;
                        navigate("/crewperformances/new");
                      }}
                    >
                      <AddCircleOutline />
                    </StyledButton>
                  </span>
                </Tooltip>
              </Box>
            )}

            <ToggleButtonGroup
              aria-label="View Toggle"
              value={view}
              exclusive
              onChange={(e, value) => value && setView(value)}
            >
              {[
                { value: "crewAssessment", icon: <Assessment />, label: "Crew Assessment" },
                { value: "calcKPI", icon: <Calculate />, label: "Calculate KPI", onClick: calculateCrewKPIs },
                { value: "showKPI", icon: <Monitor />, label: "Show saved KPI", onClick: showSavedCrewKPI },
                { value: "kpiData", icon: <ChangeHistory />, label: "KPI Data", onClick: handleCrewKPIData },
              ].map(({ value, icon, onClick, label }) => (
                <Tooltip key={value} title={label} arrow>
                  <ToggleButton
                    aria-label={value}
                    size="small"
                    value={value}
                    onClick={onClick}
                  >
                    {icon}
                  </ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>      
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ""}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          
        >
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
          > 
            {view === "crewAssessment" ? (
              <CrewAssessment
                rows={crewPerformances}
                columns={columns.concat(actionColumn)}
                loading={loading}
                rowSelectionModel={rowSelectionModel}
                setRowSelectionModel={setRowSelectionModel}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/crewperformances/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : view === "kpiData" ? (
              <CrewKPIData
                rows={crewKPIData}
                onDelete={handleDeleteCrewKpi}
              />
            ) : (
              <CrewKPI
                rows={crewKPI}
                onKpiChange={handleKpiChange}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
            <DeleteConfirm
              open={confirmDialogOpen}
              onClose={() => setConfirmDialogOpen(false)}
              onConfirm={executeDelete}
              deleteType={deleteType}
            />

            {openRosterDetailsDialog &&
              <RosterDetailsDialog 
                open={openRosterDetailsDialog}
                onClose={handleCloseRosterDetailsDialog}
                crewCode={selectedCrewCode}
                crewName={selectedCrewName}
                crewRosters={groupedCrewRosters}
              />
            }
          </Box>    
        </Box>
      )}
    </Box>
  );
};

export default CrewPerformances;
